import React from "react";
import { useContainerWidth } from "./useContainerWidth";
import { useNavigationGuard } from "./useNavigationGuard";
import { useAriUpdate } from "./useAriUpdate";
import { useTabularViewState } from "./useTabularViewState";
import { useSelector } from "react-redux";
import { Button, Col, Container, Row } from "rsuite";
import PropTypes from "prop-types";
import RoomtypeTabs from "./../../components/roomtype-tabs/RoomtypeTabs";
import TabularViewModals from "./TabularViewModals";
import {
  usePropertyStore,
  useIsEditableRateplan,
} from "../../contexts/PropertyStoreContext";
import Loader from "../../components/Loader";
import { useAriContext } from "../../contexts/AriContext";
import { selectManageARIDateRange } from "../../slices/ariDateRangeSlice";
import "./tabularview.scss";
import ariSelectRateplan from "../../assets/img/ari-select-rateplan.png";
import {
  selectRoomtypes,
  selectSelectedRoomtype,
} from "../../slices/roomtypeSlice";
import { dateToYMD, preprocessAriData } from "./TabularViewUtils";
import TabularViewBanners from "./TabularViewBanners";
import TabularViewTable from "./TabularViewTable";

const TabularView = ({ rateplan }) => {
  const defaultCurrency = rateplan.data.currencyCode;
  const { refetchAriData, loading } = useAriContext();
  const { selectedProperty } = usePropertyStore();
  const dateRange = useSelector(selectManageARIDateRange);
  const rooms = useSelector(selectRoomtypes);
  const selectedRoomtype = useSelector(selectSelectedRoomtype);
  const isEditable = useIsEditableRateplan(rateplan);
  const { containerRef, containerWidth } = useContainerWidth();

  const {
    currentData,
    setCurrentData,
    selectedRows,
    setSelectedRows,
    errorMessage,
    hasPendingChanges,
    currentPage,
    setCurrentPage,
    handleCheckboxChange,
    handleToggleChange,
    handleReset,
  } = useTabularViewState({
    selectedRoomtype,
    rateplan,
    isEditable,
    dateRange,
  });

  const { navigationModal, tabSwitch, confirmTabSwitch, closeNavigationModal } =
    useNavigationGuard({ selectedRows, setSelectedRows });
  const {
    showModal,
    showLoader,
    setShowLoader,
    openConfirm,
    closeConfirm,
    handleConfirm,
  } = useAriUpdate({
    selectedProperty,
    rateplan,
    rooms,
    dateRange,
    selectedRoomtype,
    refetchAriData,
    resetCurrentData: setCurrentData,
    setSelectedRows,
  });
  return (
    <Container
      className="manageari-page"
      data-testid="tabular-view-component"
      ref={containerRef}
    >
      {loading && <Loader loading={loading} />}
      <Row>
        <Col xs={24}>
          <RoomtypeTabs
            rateplanId={rateplan?.value}
            startDate={
              dateRange?.length > 1 ? dateToYMD(dateRange[0]) : undefined
            }
            endDate={
              dateRange?.length > 1 ? dateToYMD(dateRange[1]) : undefined
            }
            render={(ariData) => {
              const hasEditableRows =
                preprocessAriData({
                  data: ariData,
                  currentData,
                  isEditable,
                  selectedRoomtype,
                  dateRange,
                  defaultCurrency,
                }).length > 0;
              return (
                <>
                  {dateRange?.length > 1 ? (
                    <>
                      <TabularViewBanners
                        propertyId={selectedProperty.propertyId}
                        hasError={errorMessage}
                        isEditable={isEditable}
                      />
                      <TabularViewTable
                        ariData={ariData}
                        currentData={currentData}
                        selectedRows={selectedRows}
                        setCurrentData={setCurrentData}
                        setSelectedRows={setSelectedRows}
                        isEditable={isEditable}
                        containerWidth={containerWidth}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSize={100}
                        containerRef={containerRef}
                        selectedRoomtype={selectedRoomtype}
                        dateRange={dateRange}
                        defaultCurrency={defaultCurrency}
                        handleToggleChange={handleToggleChange}
                        handleCheckboxChange={handleCheckboxChange}
                      />
                      {isEditable && hasEditableRows && (
                        <div className="manageari-btn-container">
                          <Button
                            appearance="primary"
                            onClick={() => handleReset(ariData)}
                            className="manageari-reset"
                          >
                            Reset
                          </Button>
                          <Button
                            appearance="primary"
                            onClick={openConfirm}
                            className="manageari-save"
                            disabled={errorMessage || !hasPendingChanges}
                          >
                            Save Changes
                          </Button>
                        </div>
                      )}
                      <TabularViewModals
                        showConfirmSave={showModal}
                        onCloseConfirmSave={closeConfirm}
                        onConfirmSave={() =>
                          handleConfirm({ ariData, currentData, selectedRows })
                        }
                        showConfirmNavigation={navigationModal}
                        onCloseConfirmNavigation={closeNavigationModal}
                        onConfirmNavigation={confirmTabSwitch}
                        showLoader={showLoader}
                        setShowLoader={setShowLoader}
                        headerMessage="Saving ARI updates"
                      />
                    </>
                  ) : (
                    <img
                      src={ariSelectRateplan}
                      alt="select rateplan"
                      className="mx-auto d-block"
                    />
                  )}
                </>
              );
            }}
            onConfirmTabChange={tabSwitch}
          />
        </Col>
      </Row>
    </Container>
  );
};

TabularView.propTypes = {
  rateplan: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    data: PropTypes.shape({
      currencyCode: PropTypes.string,
    }),
  }),
};

export default TabularView;
